import { supabase } from '@/integrations/supabase/client';

export interface Invitation {
  id: string;
  email: string;
  role: 'admin' | 'atendente' | 'prestador';
  invited_by: string;
  inviter_locale: string;
  expires_at: string;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  criado_em: string;
  organization_id: string;
}

export async function listPendingInvitations(): Promise<Invitation[]> {
  const { data, error } = await supabase
    .from('invitations')
    .select('*')
    .eq('status', 'pending')
    .order('criado_em', { ascending: false });
  if (error) throw error;
  return (data ?? []) as Invitation[];
}

type FunctionInvokeResult<T> = { data: T | null; error: unknown };

function extractErrorCode(res: FunctionInvokeResult<unknown>): string {
  const e = res.error as { context?: { error?: string }; message?: string } | null;
  return e?.context?.error ?? e?.message ?? 'GENERIC';
}

export async function sendInvitation(params: {
  email: string;
  role: 'admin' | 'atendente' | 'prestador';
  locale: string;
}): Promise<{ ok: boolean; invitation_id?: string; warning?: string }> {
  const res = await supabase.functions.invoke('send-invitation', { body: params });
  if (res.error) throw new Error(extractErrorCode(res));
  const data = res.data as { error?: string } & Record<string, unknown>;
  if (data?.error) throw new Error(data.error);
  return data as { ok: boolean; invitation_id?: string; warning?: string };
}

export async function revokeInvitation(params: { invitation_id: string }): Promise<void> {
  const res = await supabase.functions.invoke('revoke-invitation', { body: params });
  if (res.error) throw res.error;
}

export async function resendInvitation(params: { invitation_id: string }): Promise<void> {
  const res = await supabase.functions.invoke('resend-invitation', { body: params });
  if (res.error) throw res.error;
}

/**
 * Validate an invitation token. Returns the edge function response as-is
 * (caller reads `.state` and narrows).
 */
export async function validateInvitationToken(token: string): Promise<Record<string, unknown> | null> {
  const res = await supabase.functions.invoke('accept-invitation', {
    body: { token, action: 'validate' },
  });
  if (res.error || !res.data) return null;
  return res.data as Record<string, unknown>;
}

/**
 * Accept an invitation (creates auth user + org membership server-side).
 */
export async function acceptInvitationWithPassword(params: {
  token: string;
  password: string;
}): Promise<Record<string, unknown>> {
  const res = await supabase.functions.invoke('accept-invitation', {
    body: { token: params.token, action: 'create', password: params.password },
  });
  const data = res.data as Record<string, unknown> | null;
  if (res.error || !data?.ok) {
    const code = (data?.error as string) || (res.error as Error)?.message || 'GENERIC';
    throw new Error(code);
  }
  return data;
}

/**
 * Returns true if the currently authenticated user has at least one
 * organization membership. Used by the accept-invitation page to block
 * acceptance by users already in another org.
 */
export async function currentUserHasAnyMembership(userId: string): Promise<boolean> {
  const { data } = await supabase
    .from('organization_members')
    .select('organization_id')
    .eq('user_id', userId);
  return (data?.length ?? 0) > 0;
}
